// ---------------------------------------------------------------------------
// C95-C100: Offer Builder Hooks — proposals, tracking, AI gen, payments, e-sign
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

const PROPOSALS_BASE_PATH = "/crm/proposals";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OfferType = "proposal" | "quote" | "invoice" | "package" | "estimate" | "coupon" | "bundle" | "gift_card" | "subscription";
export type ProposalStatus = "draft" | "sent" | "viewed" | "signed" | "declined" | "expired";
export type PaymentStatus = "unpaid" | "partial" | "paid";

export interface Proposal {
  id: string;
  title: string;
  status: ProposalStatus;
  offerType: OfferType;
  personId: string;
  personName: string;
  dealId: string | null;
  totalValue: number | null;
  currency: string;
  trackingToken: string | null;
  viewCount: number;
  paymentStatus: PaymentStatus;
  paymentLinkUrl: string | null;
  aiGenerated: string;
  validUntil: string | null;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProposalDetail extends Proposal {
  sections: ProposalSection[];
  optionTiers: Array<{ id: string; name: string; description?: string; totalValueCents: number; isRecommended?: boolean }> | null;
  selectedOptionId: string | null;
  contentHash: string | null;
  templateId: string | null;
  signatureRequests: SignatureRequest[];
}

export interface ProposalSection {
  id: string;
  sectionType: string;
  title: string;
  content: Record<string, unknown>;
  pricing: Array<{ description: string; quantity: number; unitPrice: number; total: number }> | null;
  displayOrder: number;
}

export interface ProposalTemplate {
  id: string;
  name: string;
  description: string | null;
  industry: string | null;
  sections: Array<Record<string, unknown>>;
  createdAt?: string;
  updatedAt?: string;
}

export interface SignatureRequest {
  id: string;
  signerName: string;
  signerEmail: string;
  status: string;
  expiresAt: string;
  signedAt: string | null;
}

export interface ProposalAnalytics {
  totalViews: number;
  uniqueViewers: number;
  avgTimeSpentSec: number;
  sectionEngagement: Array<{ sectionId: string; title: string; viewPercentage: number }>;
  viewTimeline: Array<{ date: string; views: number }>;
}

// ---------------------------------------------------------------------------
// C95: Proposal CRUD
// ---------------------------------------------------------------------------

export function useProposals(filters?: { status?: string; offerType?: string; search?: string }) {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProposals = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      if (filters?.offerType) params.set("offerType", filters.offerType);
      if (filters?.search) params.set("q", filters.search);
      const qs = params.toString();
      const res = await apiFetch<{ data: Proposal[] }>(`${PROPOSALS_BASE_PATH}${qs ? `?${qs}` : ""}`);
      setProposals(res.data ?? []);
    } catch { setProposals([]); }
    finally { setIsLoading(false); }
  }, [filters?.status, filters?.offerType, filters?.search]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);
  return { proposals, isLoading, refetch: fetchProposals };
}

export function useProposalDetail(proposalId: string | undefined) {
  const [proposal, setProposal] = useState<ProposalDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!proposalId) { setProposal(null); return; }
    setIsLoading(true);
    apiFetch<{ data: ProposalDetail }>(`${PROPOSALS_BASE_PATH}/${proposalId}`)
      .then((res) => setProposal(res.data))
      .catch(() => setProposal(null))
      .finally(() => setIsLoading(false));
  }, [proposalId]);

  return { proposal, isLoading };
}

export function useProposalTemplates(industry?: string) {
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = industry ? `?industry=${encodeURIComponent(industry)}` : "";
    apiFetch<{ data: ProposalTemplate[] }>(`${PROPOSALS_BASE_PATH}/templates${query}`)
      .then((res) => setTemplates(res.data ?? []))
      .catch(() => setTemplates([]))
      .finally(() => setIsLoading(false));
  }, [industry]);

  return { templates, isLoading };
}

export async function createProposal(data: {
  title?: string;
  personId?: string;
  customerName?: string;
  customerEmail?: string;
  dealId?: string;
  templateId?: string;
  offerType?: OfferType;
  currency?: string;
}): Promise<Proposal> {
  const res = await apiFetch<{ data: Proposal }>(PROPOSALS_BASE_PATH, {
    method: "POST", body: JSON.stringify(data),
  });
  return res.data;
}

export async function updateProposal(proposalId: string, data: Partial<{
  title: string;
  status: string;
  totalValue: number;
  validUntil: string;
  optionTiers: Array<{ id: string; name: string; totalValueCents: number; isRecommended?: boolean }>;
}>): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/${proposalId}`, {
    method: "PUT", body: JSON.stringify(data),
  });
}

export async function deleteProposal(proposalId: string): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/${proposalId}`, { method: "DELETE" });
}

export async function duplicateProposal(proposalId: string): Promise<ProposalDetail> {
  const res = await apiFetch<{ data: ProposalDetail }>(`${PROPOSALS_BASE_PATH}/${proposalId}/duplicate`, { method: "POST" });
  return res.data;
}

export async function sendProposal(proposalId: string): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/${proposalId}/send`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// C96: Section CRUD
// ---------------------------------------------------------------------------

export async function addSection(proposalId: string, data: {
  sectionType: string;
  title: string;
  content?: Record<string, unknown>;
  displayOrder?: number;
}): Promise<ProposalSection> {
  const res = await apiFetch<{ data: ProposalSection }>(`${PROPOSALS_BASE_PATH}/${proposalId}/sections`, {
    method: "POST", body: JSON.stringify(data),
  });
  return res.data;
}

export async function updateSection(sectionId: string, data: Partial<{
  title: string;
  content: Record<string, unknown>;
  pricing: Array<{ description: string; quantity: number; unitPrice: number; total: number }>;
  displayOrder: number;
}>): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/sections/${sectionId}`, {
    method: "PUT", body: JSON.stringify(data),
  });
}

export async function deleteSection(sectionId: string): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/sections/${sectionId}`, { method: "DELETE" });
}

export async function reorderSections(proposalId: string, sectionIds: string[]): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/${proposalId}/sections/reorder`, {
    method: "PUT", body: JSON.stringify({ sectionIds }),
  });
}

// ---------------------------------------------------------------------------
// C97: AI proposal generation
// ---------------------------------------------------------------------------

export async function generateProposal(dealId: string): Promise<ProposalDetail> {
  const res = await apiFetch<{ data: ProposalDetail }>(`${PROPOSALS_BASE_PATH}/ai-generate`, {
    method: "POST", body: JSON.stringify({ dealId }),
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// C98: Payment integration
// ---------------------------------------------------------------------------

export async function createPaymentLink(proposalId: string): Promise<{ paymentLinkUrl: string }> {
  const res = await apiFetch<{ data: { paymentLinkUrl: string } }>(`${PROPOSALS_BASE_PATH}/${proposalId}/payment-link`, { method: "POST" });
  return res.data;
}

// ---------------------------------------------------------------------------
// C99: Proposal tracking + analytics
// ---------------------------------------------------------------------------

export function useProposalAnalytics(proposalId: string | undefined) {
  const [analytics, setAnalytics] = useState<ProposalAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!proposalId) { setAnalytics(null); return; }
    setIsLoading(true);
    apiFetch<{ data: ProposalAnalytics }>(`${PROPOSALS_BASE_PATH}/${proposalId}/analytics`)
      .then((res) => setAnalytics(res.data))
      .catch(() => setAnalytics(null))
      .finally(() => setIsLoading(false));
  }, [proposalId]);

  return { analytics, isLoading };
}

// ---------------------------------------------------------------------------
// C100: E-signature
// ---------------------------------------------------------------------------

export async function requestSignature(proposalId: string, data: {
  signerEmail: string;
  signerName: string;
  expiresInDays?: number;
}): Promise<SignatureRequest> {
  const res = await apiFetch<{ data: SignatureRequest }>(`${PROPOSALS_BASE_PATH}/${proposalId}/signature-request`, {
    method: "POST", body: JSON.stringify(data),
  });
  return res.data;
}

export async function sendSignatureReminder(requestId: string): Promise<void> {
  await apiFetch(`${PROPOSALS_BASE_PATH}/signatures/${requestId}/remind`, { method: "POST" });
}
